<template>
  <div :id="module && module.id">
    <CRFormRenderer
      v-if="formConfig"
      :module="module"
      :form-config="formConfig"
      :is-submitting="isSubmitting"
      :global-error="genericBackendError"
      :is-submit-successful="submitSuccess"
      :external-field-errors="externalFieldErrors"
      @submit="onSubmit"
    />
  </div>
</template>

<script setup lang="ts">
import type { PropType } from "vue"
import { nextTick } from "vue"
import { useJobRouteParams } from "~/composables/useJobRouteParams"

const props = defineProps({
  module: {
    type: Object as PropType<ContentModule>,
    required: true,
  },
  job: {
    type: Object as PropType<FlatJob>,
    required: false,
  },
})

// Get the job ID from route
const effectiveJobId = computed(() => {
  const { jobId } = useJobRouteParams()
  return jobId || null
})

// Fetch the form configuration
const { formConfig, formConfigError } = await useFormConfig(effectiveJobId.value, props.job?.group_id ?? null)

// Handle form submission
const { handleSubmit, isSubmitting, submitError, submitSuccess, externalFieldErrors, genericBackendError } = useApplicationFormData(
  formConfig.value,
  effectiveJobId.value,
  props.job?.ats_application_customer_name ?? null,
  props.job?.external_id ?? null
)

async function onSubmit(payload: { valid: boolean; values: Record<string, any>; errors: Record<string, any> }) {
  await handleSubmit(payload)

  if (!import.meta.client) return

  await nextTick()

  const root = document.getElementById(props.module?.id ?? "")
  if (!root) return

  const targetElement =
    root.querySelector<HTMLElement>(".global-error-message, .field-error")?.closest<HTMLElement>(".grid-item") ??
    root.querySelector<HTMLElement>(".global-error-message, .field-error")

  if (!targetElement) return

  targetElement.scrollIntoView({ behavior: "smooth", block: "center" })
}
</script>

<style scoped lang="scss">
.cr-form-renderer-container {
  width: 100%;
}
</style>
